import React, {useEffect, useMemo, useState} from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';

import {
  apiGetFundingPerProvincePerCrop,
  IFdFundingPerProvincePerCrop,
} from 'constants/Api';

import './FundingPerCropWidget.scss';

/* -------------------------------------------------------------------------- */
const CROP_COLORS = [
  '#267373', // Khula green
  '#f1a24a', // Orange
  '#5b8ed9', // Blue
  '#e57373', // Red
  '#67b67a', // Light green
  '#ab47bc', // Purple
  '#26a69a', // Teal
  '#8d6e63', // Brown
];

/* -------------------------------------------------------------------------- */
const CustomTooltip = ({active, payload}: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];

  return (
    <div className="fpc-tooltip">
      <div className="fpc-tooltip-title">{entry.name}</div>
      <div className="fpc-tooltip-row">
        <span
          className="fpc-dot"
          style={{backgroundColor: entry.payload.fill}}
        />
        <span className="fpc-crop">Funding ask:</span>
        <span className="fpc-val">
          R {Math.round(entry.value).toLocaleString('en-ZA')}
        </span>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
export default function FundingPerCropWidget() {
  const [data, setData] = useState<IFdFundingPerProvincePerCrop[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGetFundingPerProvincePerCrop()
      .then((res) => {
        if (alive) setData(res || []);
      })
      .catch((err) => {
        console.error('Failed to load funding per crop', err);
        if (alive) setUnavailable(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  // Aggregate total funding by crop for pie chart
  const chartData = useMemo(() => {
    const cropMap: Record<string, number> = {};

    data.forEach((row) => {
      const c = row.crop || 'Unknown';
      cropMap[c] = (cropMap[c] || 0) + row.totalFunding;
    });

    return Object.keys(cropMap)
      .map((cropName, index) => ({
        name: cropName,
        value: cropMap[cropName],
        fill: CROP_COLORS[index % CROP_COLORS.length],
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data]);

  /* ------------------------------------------------------------------------ */
  if (loading) {
    return (
      <Box className="funding-per-crop-widget">
        <Typography className="widget-heading">FUNDING BY CROP</Typography>
        <Typography className="widget-sub" variant="caption">
          Total ask broken down by crop
        </Typography>
        <Box mt={2}>
          <Skeleton variant="rect" height={220} style={{borderRadius: 8}} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="funding-per-crop-widget">
      <Box className="widget-head">
        <Typography className="widget-heading">FUNDING BY CROP</Typography>
        <Typography className="widget-sub" variant="caption">
          Total ask broken down by crop
        </Typography>
      </Box>

      {unavailable ? (
        <Typography variant="body2" color="textSecondary">
          Funding data is not yet available.
        </Typography>
      ) : chartData.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No funding data found
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={75}
              innerRadius={35}
              paddingAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{fontSize: 12, paddingTop: 10}}
              iconType="circle"
              iconSize={8}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}
